<script>
import PaymentEdit from "~/src/components/modals/PaymentEdit2026.vue";
import axios from "axios";
import { mapActions } from "vuex";

export default {
  components: { PaymentEdit },
  props: {
    itemData: {
      type: Object,
      required: true,
    },
    application: {
      type: Object,
      required: true,
    },
    label: String,
    // 1-based position in the payments ledger (shown as the row marker).
    index: {
      type: [Number, String],
      default: null,
    },
  },

  data: () => ({
    isShowEdit: false,
    canUpdate: false,
    confirmDelete: false,
    deleting: false,
  }),

  computed: {
    // A negative payment is a refund (TO'LOV QAYTARILDI); positive is a normal payment.
    isRefund() {
      return Number(this.itemData && this.itemData.paid) < 0;
    },
    formattedDate() {
      return this.formatDate(this.itemData && this.itemData.datePayment);
    },
    // Grouped sum with a real minus sign for refunds, e.g. "1 000" / "−1 000".
    formattedAmount() {
      const n = Number(this.itemData && this.itemData.paid);
      if (!isFinite(n)) return this.itemData && this.itemData.paid;
      const grouped = new Intl.NumberFormat("uz-UZ").format(Math.abs(n));
      return (n < 0 ? "−" : "") + grouped;
    },
    statusText() {
      return this.isRefund ? "TO'LOV QAYTARILDI" : "TO'LOV QILDI";
    },
    sourceText() {
      return this.formatComingFrom(this.itemData && this.itemData.comingFrom);
    },
    hasSource() {
      const s = (this.sourceText || "").trim();
      return !!s && s !== "-";
    },
    canEdit() {
      return (
        this.canUpdate &&
        !!this.itemData &&
        this.itemData.comingFrom != "2024 (qo'shimcha to'lov)"
      );
    },
    canDelete() {
      return this.canUpdate && !!this.itemData && !!this.itemData.id;
    },
  },

  async mounted() {
    this.canUpdate = await this.hasRole("KONTRAKT_26_27", "UPDATE");
  },

  methods: {
    ...mapActions("admin-contract2026", ["DELETE_PAYMENT"]),
    onShowEdit() {
      this.isShowEdit = true;
    },
    onRefresh() {
      this.$emit("refresh");
    },
    onShowDelete() {
      this.confirmDelete = true;
    },
    async onConfirmDelete() {
      if (!this.itemData?.id) return;
      this.deleting = true;
      const ok = await this.DELETE_PAYMENT(this.itemData.id);
      this.deleting = false;
      this.confirmDelete = false;
      if (ok) this.onRefresh();
    },

    formatDate(date) {
      if (!date) return "";
      const [year, month, day] = String(date).split("-");
      if (!day) return date;
      return `${day}-${month}-${year}`;
    },

    formatComingFrom(comingFrom) {
      if (comingFrom === "") {
        return " - ";
      } else if (comingFrom === "OneC") {
        return "1C";
      } else if (comingFrom == "MANUAL") {
        return "Qo'lda kiritilgan";
      } else {
        return comingFrom;
      }
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op },
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  },
};
</script>

<template>
  <div class="pay-item">
    <payment-edit
      v-if="isShowEdit"
      @close="isShowEdit = false"
      @refresh="onRefresh"
      :data="itemData"
      :contractId="$route.params.id"
    />

    <article
      class="pay-card"
      :class="isRefund ? 'pay-card--refund' : 'pay-card--paid'"
    >
      <header class="pay-card__top">
        <div class="pay-card__when">
          <span v-if="index != null" class="pay-card__index">{{ index }}</span>
          <span class="pay-card__date">{{ formattedDate }}</span>
        </div>

        <div v-if="canEdit || canDelete" class="pay-card__actions">
          <v-btn
            v-if="canEdit"
            icon
            x-small
            class="pay-card__act"
            aria-label="Tahrirlash"
            title="Tahrirlash"
            @click="onShowEdit"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="canDelete"
            icon
            x-small
            class="pay-card__act pay-card__act--danger"
            aria-label="O'chirish"
            title="O'chirish"
            @click="onShowDelete"
          >
            <v-icon small>mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </header>

      <div class="pay-card__amount">
        <span class="pay-card__value">{{ formattedAmount }}</span>
        <span class="pay-card__cur">so'm</span>
      </div>

      <footer class="pay-card__meta">
        <span class="pay-tag pay-tag--status">{{ statusText }}</span>
        <span v-if="hasSource" class="pay-tag pay-tag--source">{{ sourceText }}</span>
      </footer>

      <p v-if="itemData.comment" class="pay-card__comment">{{ itemData.comment }}</p>
    </article>

    <v-dialog v-model="confirmDelete" max-width="440" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t("To'lovni o'chirish?") }}</v-card-title>
        <v-card-text>
          <div>{{ $t("Bu amalni qaytarib bo'lmaydi. To'lov o'chirilgandan keyin kontrakt summalari avtomatik qayta hisoblanadi.") }}</div>
          <div class="mt-3 text-body-2" style="color:#475569">{{ label }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="confirmDelete = false">{{ $t("Bekor qilish") }}</v-btn>
          <v-btn color="red darken-1" dark :loading="deleting" :disabled="deleting" @click="onConfirmDelete">
            {{ $t("O'chirish") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.pay-item {
  margin-top: 10px;
}

.pay-card {
  --accent-strong: #475569;
  --accent-tint: rgba(148, 163, 184, 0.14);

  position: relative;
  padding: 10px 12px 11px;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  background: #fff;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.pay-card:hover {
  border-color: #d8dbe4;
  box-shadow: 0 4px 14px rgba(17, 24, 39, 0.06);
}

.pay-card--paid {
  --accent-strong: #047857;
  --accent-tint: rgba(16, 185, 129, 0.13);
}

.pay-card--refund {
  --accent-strong: #b91c1c;
  --accent-tint: rgba(239, 68, 68, 0.13);
}

.pay-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pay-card__when {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.pay-card__index {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 6px;
  background: #f1f3f7;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pay-card__date {
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.2px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pay-card__actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
  margin-right: -4px;
}

.pay-card__act ::v-deep .v-icon {
  color: #9aa1ad;
  transition: color 0.15s ease;
}

.pay-card__act:hover ::v-deep .v-icon,
.pay-card__act:focus-visible ::v-deep .v-icon {
  color: #334155;
}

.pay-card__act--danger:hover ::v-deep .v-icon,
.pay-card__act--danger:focus-visible ::v-deep .v-icon {
  color: #dc2626;
}

.pay-card__amount {
  margin-top: 6px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 5px;
  line-height: 1.1;
}

.pay-card__value {
  font-size: 1.18rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  font-variant-numeric: tabular-nums;
  color: var(--accent-strong);
  white-space: nowrap;
}

.pay-card__cur {
  font-size: 0.72rem;
  font-weight: 600;
  color: #9aa1ad;
  letter-spacing: 0.3px;
}

.pay-card__meta {
  margin-top: 9px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.pay-tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1;
  white-space: nowrap;
}

.pay-tag--status {
  padding: 5px 9px;
  background: var(--accent-tint);
  color: var(--accent-strong);
}

/* "Qo'lda kiritilgan" / "1C" — original soft gradient pill (theme-agnostic, as before). */
.pay-tag--source {
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: normal;
  line-height: normal;
  background: linear-gradient(135deg, #f9fafb, #f3f4f6);
  color: #111827;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.pay-card__comment {
  margin: 8px 0 0;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #64748b;
  word-break: break-word;
}

/* Dark theme */
.theme--dark .pay-card {
  background: #1e1f26;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .pay-card:hover {
  border-color: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}

.theme--dark .pay-card--paid {
  --accent-strong: #34d399;
  --accent-tint: rgba(16, 185, 129, 0.18);
}

.theme--dark .pay-card--refund {
  --accent-strong: #f87171;
  --accent-tint: rgba(239, 68, 68, 0.2);
}

.theme--dark .pay-card__index {
  background: rgba(255, 255, 255, 0.08);
  color: #b4b7c2;
}

.theme--dark .pay-card__date {
  color: #9ca3af;
}

.theme--dark .pay-card__cur {
  color: #7b8190;
}

.theme--dark .pay-card__comment {
  color: #9ca3af;
}

.theme--dark .pay-card__act ::v-deep .v-icon {
  color: #8b8d9e;
}

.theme--dark .pay-card__act:hover ::v-deep .v-icon,
.theme--dark .pay-card__act:focus-visible ::v-deep .v-icon {
  color: #e4e5eb;
}

@media (prefers-reduced-motion: reduce) {
  .pay-card,
  .pay-card__act ::v-deep .v-icon {
    transition: none;
  }
}
</style>
